import { ParsedUrlQuery } from 'querystring';

import { SubscriptionApiResponse } from 'src/services/responseTypes/subscriptionApiResponse';
import {
  OfferImage,
  OptionState,
  Paymentprovider,
  PlanCategory
} from 'src/types/subscription';

import {
  PromoResponseItem,
  ProductDetail
} from '../../services/responseTypes/applyPromoResponse';
import {
  Data,
  PaymentProvider,
  Plan
} from '../../services/responseTypes/displayPlansResponse';
import { PlansMetaData } from '../../services/responseTypes/plansMetaDataResponse';
import { subscriptionDataType } from '../../store/interface';
import type {
  Data as SubsData,
  Option,
  SubscriptionState
} from '../../types/subscription';
import { mixpanelObject } from '../analytics';
import {
  MIXPANEL_ACTUAL_COST,
  MIXPANEL_COST,
  MIXPANEL_CURRENT_SUBSCRIPTION,
  MIXPANEL_ELEMENT,
  MIXPANEL_PACK_SELECTED,
  MIXPANEL_TRANSACTION_CURRENCY
} from '../analytics/mixpanel/mixpanelConstants';
import { timeEvent } from '../analytics/mixpanel/mixpanelEventsHelper';
import {
  deleteLocalStorage,
  getCookie,
  getLocalStorage,
  setCookie,
  setLocalStorage
} from '../browserStorageHandler';
import { globalUrlHelper, parseJwt } from '../common';
import { ActivePromotions, DynamicObject } from '../commonTypes';
import { CLOUDINARY_IMAGE_URL } from '../constants/ApiConstants';
import { REFERRAL_DISCOUNT_EXPIRY, REFERRER } from '../constants/AppConstants';
import { PAYMENT_PROVIDER } from '../constants/cookieConstants';
import {
  CONSUMPTION_JOURNEY_CODE_REDEMPTION_OBJ,
  CONSUMPTION_PAYMENT_JOURNEY_PATH,
  IS_CONSUMPTION_PAYMENT_JOURNEY,
  SUBSCRIPTION_ORDER_SUMMARY_BANNER,
  REFERRAL_CODE,
  REFERRAL_ID,
  REFERRAL_NAME,
  SELECTED_PLAN,
  PAYMENT_OBJECT,
  REFERRAL_USERID,
  USER_TOKEN,
  PAYMENT_JOURNEY_SOURCE,
  ACTUAL_PRICE
} from '../constants/localStorageConstants';
import { MONTH_NAMES_FULL } from '../constants/MonthNames';
import { DOLLAR_SYMBOL, RUPEE_SYMBOL } from '../constants/stringConstants';
import { TAG_NAME } from '../constants/TagNameConstants';
import {
  GLOBAL_PAYMENT_PROVIDERS,
  GLOBAL_PAYMENT_PROVIDERS_INTERFACE
} from '../constants/utilConstants';
import { dateSuffix } from '../convertTimeToHumanReadableFormatUtil';
import { getImagesDimentionByResolution } from '../ImageHelper/ImagesDimensionHelper';
import {
  getWebPImageURLParams,
  ImageCompatibleTypes
} from '../ImageHelper/ImageURLGenerator';

export const calculateSellPrice = (
  plan: Plan,
  activePromos: ActivePromotions,
  consumptionBuyPlan?: boolean
) => {
  if (activePromos[plan.id]) {
    if (consumptionBuyPlan) {
      let promoDiscount = 0;
      const { discount, discount_type: discountType = '' } =
        activePromos[plan.id].promotions[0];
      if (discountType.toLowerCase() === 'currency') {
        promoDiscount = Number(discount) ? discount : 0;
      } else if (discountType.toLowerCase() === 'percentage') {
        promoDiscount = (discount * activePromos[plan.id].price) / 100;
      }
      return (activePromos[plan.id].price - promoDiscount).toFixed(2);
    } else {
      return activePromos[plan.id]?.actualPrice;
    }
  }
  if (plan?.discount?.code) {
    return plan.actual_price;
  }
  return plan.sell_price;
};

export const calculateSellPriceNew = (
  plan: Option,
  activePromos: ActivePromotions,
  consumptionBuyPlan?: boolean
) => {
  //display = market
  //sell = actual
  if (activePromos[plan.id]) {
    if (consumptionBuyPlan) {
      let promoDiscount = 0;
      const { discount, discount_type: discountType = '' } =
        activePromos[plan.id].promotions[0];
      if (discountType.toLowerCase() === 'currency') {
        promoDiscount = Number(discount) ? discount : 0;
      } else if (discountType.toLowerCase() === 'percentage') {
        promoDiscount = (discount * activePromos[plan.id].price) / 100;
      }
      return (activePromos[plan.id].price - promoDiscount).toFixed(2);
    } else {
      return activePromos[plan.id]?.actualPrice;
    }
  }
  if (plan?.price?.code) {
    return plan.price.actual;
  }
  return plan.price.actual;
};

export const calculateDisplayPriceNew = (
  plan: Option,
  activePromos: ActivePromotions,
  consumptionBuyPlan?: boolean
) => {
  if (activePromos[plan.id]) {
    if (consumptionBuyPlan) {
      return activePromos[plan.id].price;
    } else {
      return activePromos[plan.id].sellPrice;
    }
  }
  return plan.price?.market;
};

export const calculateDisplayPrice = (
  plan: Plan,
  activePromos: ActivePromotions,
  consumptionBuyPlan?: boolean
) => {
  if (activePromos[plan.id]) {
    if (consumptionBuyPlan) {
      return activePromos[plan.id].price;
    } else {
      return activePromos[plan.id].sellPrice;
    }
  }
  return plan.display_price;
};

export const calculateDiscountPercentage = (
  plan: Plan,
  activePromos: ActivePromotions,
  consumptionBuyPlan?: boolean
) => {
  return Math.floor(
    ((calculateDisplayPrice(plan, activePromos, consumptionBuyPlan) -
      Number(calculateSellPrice(plan, activePromos, consumptionBuyPlan))) *
      100) /
      calculateDisplayPrice(plan, activePromos, consumptionBuyPlan)
  );
};

export const getPromoDiscount = (promo: PromoResponseItem) => {
  let promoDiscount = 0;
  const { discount, discount_type: discountType = '' } = promo.promotions[0];
  if (discountType.toLowerCase() === 'currency') {
    promoDiscount = Number(discount) ? discount : 0;
  } else if (discountType.toLowerCase() === 'percentage') {
    promoDiscount = (discount * promo.price) / 100;
  }
  return Math.round((promoDiscount / promo.price) * 100) + '%';
};

/**
 * Method for calculating discount as per New Promotion API response
 */
export const getPromoDiscountNew = (promo: ProductDetail) => {
  const { discount, sellPrice } = promo;
  return Math.round((discount / sellPrice) * 100) + '%';
};

export const getCurrencySymbol = (currency: string) => {
  switch (currency) {
    case 'INR':
      return RUPEE_SYMBOL;
    case 'USD':
      return DOLLAR_SYMBOL;
    default:
      return currency;
  }
};

export const filterGlobalPlans = (plans: Plan[]) => {
  const paymentProvider = getCookie(PAYMENT_PROVIDER);
  if (!paymentProvider) {
    return plans;
  }
  const subscriptionPlans: Plan[] = plans.filter((plan: Plan) => {
    // If paymentprovider mentioned in URL, assigning that paymentProvider to show in FE
    return paymentFilter(plan.payment_providers);
  });
  return subscriptionPlans;
};

const paymentFilter = (paymentProviders: PaymentProvider[]) => {
  const paymentProvider = getCookie(PAYMENT_PROVIDER)
    ?.toString()
    .toLowerCase() as keyof GLOBAL_PAYMENT_PROVIDERS_INTERFACE;
  const globalPaymentProviders = Object.keys(GLOBAL_PAYMENT_PROVIDERS);
  return paymentProviders.some((provider: PaymentProvider) => {
    if (
      (globalPaymentProviders.includes(paymentProvider) &&
        GLOBAL_PAYMENT_PROVIDERS[paymentProvider].includes(
          provider.name.toLowerCase()
        )) ||
      provider.name.toLowerCase() === paymentProvider
    ) {
      return true;
    }
  });
};

// return url parameters as an object
export const getUrlParamsAsObject = (url: string) => {
  let queryStartIndex = 0;
  if (url && decodeURI(url).indexOf('?') > -1) {
    queryStartIndex = decodeURI(url).indexOf('?') + 1;
  }

  let queryParams = url.split('&');
  queryParams = queryParams.filter((item) => !!item);
  url = queryParams.join('&');

  const urlParams = [
    'tag=',
    't=',
    'utm_source=',
    'utm_medium=',
    'utm_campaign=',
    'utm_term=',
    'utm_content=',
    'dekey=',
    'sid=',
    'partner_platform=',
    'partner=',
    'plid=',
    'pp=',
    'ctma_id='
  ];

  if (
    (url && url.indexOf('code') > -1 && url.indexOf('autoapply') > -1) ||
    urlParams.some((param: string) => url && url.indexOf(param) > -1)
  ) {
    return JSON.parse(
      `{"${decodeURI(url)
        .substring(queryStartIndex)
        .replace(/"/g, '\\"')
        .replace(/&/g, '","')
        .replace(/=/g, '":"')}"}`
    );
  }
  return {};
};

export const getCurrentPlan = (
  plansData: Data | null,
  selectedPlanID: string
) => {
  return plansData?.plans?.find((plan) => plan.id === selectedPlanID) || null;
};

export const getCurrentPlanNew = (plansData?: SubscriptionState[]) => {
  const currentPlan =
    plansData
      ?.find((item) => item.active)
      ?.options?.find((item) => item.active) ?? null;
  return currentPlan;
};

export const getSpecialPlanNew = (plansData?: SubscriptionState[]) => {
  const defaultPlan =
    plansData
      ?.find((item) => item.isDefault)
      ?.options.find((item) => item.isDefault) ?? null;
  return defaultPlan;
};
/**
 * return plan by id
 * required to interface with old flow components
 * */
export const getPlanById = (
  planId: string,
  plansData?: SubscriptionState[]
) => {
  const plan =
    plansData
      ?.flatMap((item) => item.options)
      ?.find((option) => option.id === planId) ?? null;
  return plan;
};

export const getSpecialPlan = (plans: Plan[] = [], defaultPlan: string) => {
  return (
    (plans.length && plans.find((plan) => plan.id === defaultPlan)) || null
  );
};

// function for creating the plan object that can be used by payment page
export const setPaymentData = (
  data: any,
  flags: { [key: string]: boolean },
  promo_code: string | null,
  country: string,
  isGlobalUpgrade: boolean
) => {
  const {
    BillDesk,
    PayTM,
    PayU,
    Mobikwik,
    Phonepe,
    Amazonpay_new: AmazonpayNew,
    Google,
    Qwikcilver,
    PaytmQR
  } = flags;
  const paymentProvider = getCookie(PAYMENT_PROVIDER)
    ?.toString()
    .toLowerCase() as keyof GLOBAL_PAYMENT_PROVIDERS_INTERFACE;
  let payProviders = data.paymentProviders || data.payment_providers; // Setting all payment providers available to that region by default
  // If paymentprovider mentioned in URL, assigning that paymentProvider to show in FE
  if (paymentProvider && country !== 'IN') {
    payProviders = [];
    const globalPaymentProviders = Object.keys(GLOBAL_PAYMENT_PROVIDERS);
    (data.paymentProviders || data.payment_providers).forEach(
      (item: PaymentProvider) => {
        if (
          (globalPaymentProviders.includes(paymentProvider) &&
            GLOBAL_PAYMENT_PROVIDERS[paymentProvider].includes(
              item.name.toLowerCase()
            )) ||
          item.name.toLowerCase() === paymentProvider
        ) {
          payProviders.push(item);
        }
      }
    );
    if (payProviders.length === 0)
      payProviders = data.paymentProviders || data.payment_providers;
  }
  return {
    pack: data.title || '',
    price:
      ((data?.discount?.code || country.toLowerCase() != 'in') &&
        data.actual_price) ||
      data.sellPrice ||
      data.sell_price ||
      data.price,
    currency: data.currency || '',
    pack_id: data.id || '',
    start_date: data.start || '',
    end_date: data.end || '',
    billing_frequency: data.billingFrequency || data.billing_frequency || '',
    country: country || data.country || '',
    device_number:
      data.numberOfSupportedDevices || data.number_of_supported_devices || -1,
    all_paymentProviders: payProviders,
    promotions: data.promotions,
    auto_renewal: data.recurring,
    tv_lang: data.tv_show_audio_languages,
    channel_lang: data.channel_audio_languages,
    movie_lang: data.movie_audio_languages,
    description: data.description || '',
    free_trial: data.freeTrial || data.free_trial || '',
    pack_type:
      data.subscriptionPlanType ||
      data.subscription_plan_type ||
      data.plan_type ||
      data.planType,
    promo_code,
    BillDesk,
    PayTM,
    PayU,
    Mobikwik,
    Phonepe,
    AmazonpayNew,
    Google,
    Qwikcilver,
    PaytmQR,
    billing_type: data.billingType || data.billing_type || '',
    allowed_playback_duration:
      data.allowedPlaybackDuration || data.allowed_playback_duration || '',
    offers: data.offers || [],
    tvodPackDetails: data.tvodPackDetails || {},
    duration_text:
      data.displayPrice ||
      data.display_price ||
      data.durationText ||
      data.duration_text ||
      '',
    original_price: data.original_price || data.actual_price || '',
    sell_price: data.sell_price,
    suggestionTag: data.suggession_tag,
    title: data.title,
    features: data.features,
    purchaseAllowed: data.purchase_allowed,
    priority: data.priority,
    isDefault: data.isDefault,
    duration: data.duration,
    payablePrice: data.sell_price,
    isGlobalUpgrade,
    sellPrice: data?.sell_price || data?.sellPrice
  };
};

export const buyPlan = (
  plansData: Data | null,
  plansMetaData: PlansMetaData[],
  currentPlan: Plan | null,
  planId: string,
  activePromos: ActivePromotions,
  promoCode: string,
  country: string,
  currentSubscription?: React.MutableRefObject<SubscriptionApiResponse | null> | null,
  consumptionBuyPlan?: boolean
) => {
  if (!plansData || !planId) return null;
  const selectedPlanId = planId;
  let promoDiscount;
  const selectedPlanMetadata = plansMetaData.find(
    (plan: PlansMetaData) => plan.id === selectedPlanId
  );
  const selectedPlanPayload: any = {
    ...(activePromos[selectedPlanId] || currentPlan),
    ...selectedPlanMetadata
  };

  // create dictionary of payment provider flags
  const paymentProviderFlags: { [key: string]: boolean } = {};

  const paymentProvider = getCookie(PAYMENT_PROVIDER)
    ?.toString()
    .toLowerCase() as keyof GLOBAL_PAYMENT_PROVIDERS_INTERFACE;
  if (country !== 'IN' && paymentProvider) {
    // If paymentprovider mentioned in URL, assigning that paymentProvider to show in FE
    const globalPaymentProviders = Object.keys(GLOBAL_PAYMENT_PROVIDERS);
    (
      selectedPlanPayload.paymentProviders ||
      selectedPlanPayload.payment_providers
    ).forEach((provider: PaymentProvider) => {
      if (
        (globalPaymentProviders.includes(paymentProvider) &&
          GLOBAL_PAYMENT_PROVIDERS[paymentProvider].includes(
            provider.name.toLowerCase()
          )) ||
        provider.name.toLowerCase() === paymentProvider
      ) {
        paymentProviderFlags[provider.name] = true;
      }
    });
  } else {
    const {
      all_paymentProviders = [],
      paymentProviders = [],
      payment_providers = []
    } = selectedPlanPayload;
    (paymentProviders || payment_providers || all_paymentProviders).forEach(
      (paymentProvider: PaymentProvider) => {
        paymentProviderFlags[paymentProvider.name] = true;
      }
    );
  }

  let promo_code = null;
  if (activePromos[selectedPlanId]) {
    promo_code = promoCode;
  }

  const isGlobalUpgrade: boolean =
    currentSubscription &&
    currentSubscription.current &&
    currentSubscription.current.id &&
    currentSubscription.current.subscription_plan &&
    country.toLowerCase() !== 'in'
      ? true
      : false;

  // format plans' data
  const selectedPlanData = setPaymentData(
    selectedPlanPayload,
    paymentProviderFlags,
    promo_code,
    country,
    isGlobalUpgrade
  );

  if (promoCode && currentPlan) {
    promoDiscount = {
      discount:
        currentPlan.sell_price -
        Number(
          calculateSellPrice(currentPlan, activePromos, consumptionBuyPlan)
        )
    };
  }

  if (currentPlan?.discount) {
    promoDiscount = { discount: currentPlan.discount };
  }

  setLocalStorage(
    SELECTED_PLAN,
    promoDiscount?.discount
      ? { ...selectedPlanData, ...promoDiscount }
      : selectedPlanData
  );
};
export const buyPlanNew = (
  plansData: SubscriptionState[] | null,
  plansMetaData: PlansMetaData[],
  currentPlan: Plan | null,
  planId: string,
  activePromos: ActivePromotions,
  promoCode: string,
  country: string,
  currentSubscription?: React.MutableRefObject<SubscriptionApiResponse | null> | null,
  consumptionBuyPlan?: boolean
) => {
  if (!plansData || !plansData.length || !planId) return null;
  const selectedPlan = plansData
    .find((plan) => plan.active)
    ?.options.find((option) => option.active);
  const { price, duration, id: selectedPlanId = '' } = selectedPlan ?? {};
  if (!selectedPlanId) return;

  let promoDiscount;

  const selectedPlanPayload: any = {
    ...(activePromos[selectedPlanId] || currentPlan),
    ...{
      ...selectedPlan,
      actual_price: price?.actual,
      price: price?.actual,
      sell_price: price?.renewal,
      currency: price?.currency,
      billingFrequency: duration?.frequency,
      duration: duration?.label,
      duration_text: price?.renewal,
      purchaseAllowed: !!selectedPlan?.purchaseAllowed
    }
  };

  // create dictionary of payment provider flags
  const paymentProviderFlags: { [key: string]: boolean } = {};

  const paymentProvider = getCookie(PAYMENT_PROVIDER)
    ?.toString()
    .toLowerCase() as keyof GLOBAL_PAYMENT_PROVIDERS_INTERFACE;
  if (country !== 'IN' && paymentProvider) {
    // If paymentprovider mentioned in URL, assigning that paymentProvider to show in FE
    const globalPaymentProviders = Object.keys(GLOBAL_PAYMENT_PROVIDERS);
    (
      selectedPlanPayload.paymentProviders ||
      selectedPlanPayload.payment_providers
    ).forEach((provider: PaymentProvider) => {
      if (
        (globalPaymentProviders.includes(paymentProvider) &&
          GLOBAL_PAYMENT_PROVIDERS[paymentProvider].includes(
            provider.name.toLowerCase()
          )) ||
        provider.name.toLowerCase() === paymentProvider
      ) {
        paymentProviderFlags[provider.name] = true;
      }
    });
  } else {
    const {
      all_paymentProviders = [],
      paymentProviders = [],
      payment_providers = []
    } = selectedPlanPayload;
    (paymentProviders || payment_providers || all_paymentProviders).forEach(
      (paymentProvider: PaymentProvider) => {
        paymentProviderFlags[paymentProvider.name] = true;
      }
    );
  }

  const promo_code = activePromos[selectedPlanId] ? promoCode : null;

  const isGlobalUpgrade: boolean =
    currentSubscription &&
    currentSubscription.current &&
    currentSubscription.current.id &&
    currentSubscription.current.subscription_plan &&
    country.toLowerCase() !== 'in'
      ? true
      : false;

  // format plans' data
  const selectedPlanData = setPaymentData(
    selectedPlanPayload,
    paymentProviderFlags,
    promo_code,
    country,
    isGlobalUpgrade
  );

  if (promoCode && selectedPlan) {
    promoDiscount = {
      discount:
        selectedPlan.price.actual -
        Number(
          calculateSellPriceNew(selectedPlan, activePromos, consumptionBuyPlan)
        )
    };
  }

  if (currentPlan?.discount) {
    promoDiscount = { discount: currentPlan.discount };
  }

  setLocalStorage(
    SELECTED_PLAN,
    promoDiscount?.discount
      ? { ...selectedPlanData, ...promoDiscount }
      : selectedPlanData
  );
};

export const getBgDimension = (dimensionType: string, deviceWidth: number) => {
  const imageDimensionObj = getImagesDimentionByResolution(deviceWidth);
  if (dimensionType === 'height')
    return imageDimensionObj['portraitSmallBg'][1];
  return imageDimensionObj['portraitSmallBg'][0];
};

export const combineSimilarPlans = (plans: Plan[]) => {
  const uniquePlans: Plan[] = [];
  plans.forEach((plan: Plan) => {
    const uniquePlanIndex = uniquePlans.findIndex((uniqueplan: Plan) => {
      return uniqueplan.title === plan.title;
    });
    if (uniquePlanIndex > -1) {
      uniquePlans[uniquePlanIndex] =
        uniquePlans[uniquePlanIndex].sell_price > plan.sell_price
          ? uniquePlans[uniquePlanIndex]
          : plan;
    } else {
      uniquePlans.push(plan);
    }
  });
  return uniquePlans;
};

export const setGlobalPaymentProviders = (paramObject: ParsedUrlQuery) => {
  const globalPaymentProviders = Object.keys(GLOBAL_PAYMENT_PROVIDERS);
  if (
    paramObject.pp &&
    typeof paramObject.pp === 'string' &&
    (globalPaymentProviders.includes(paramObject.pp) ||
      GLOBAL_PAYMENT_PROVIDERS.simpaisa.includes(
        paramObject.pp.toLowerCase()
      ) ||
      GLOBAL_PAYMENT_PROVIDERS.cc.includes(paramObject.pp.toLowerCase()) ||
      GLOBAL_PAYMENT_PROVIDERS.mife.includes(paramObject.pp.toLowerCase()))
  ) {
    setCookie(PAYMENT_PROVIDER, paramObject.pp, 0, 30);
  }
};

// days to month or year converter
export const getDaysInYearOrMonth = (
  days: number,
  translationNode: DynamicObject,
  showYearInMonths = false
) => {
  if (Object.keys(translationNode).length) {
    const monthCount = Math.floor(days / 30);
    if (monthCount > 1 && monthCount < 12) {
      return `${monthCount} ${translationNode.Months}`;
    }
    if (monthCount === 12) {
      return showYearInMonths
        ? `12 ${translationNode.Months}`
        : `1 ${translationNode.Year}`;
    }
    if (monthCount === 1) {
      return `1 ${translationNode.Month}`;
    }
    if (monthCount === 0) {
      return `${days} ${days > 1 ? translationNode.Days : translationNode.Day}`;
    }
  }
  return null;
};

/* Remove all localstorage flags applicable in payment journey from consumption page
   as user is going ahead with payment journey from subscription page */

export const resetConsumptionPaymentJourneyFlags = () => {
  deleteLocalStorage(IS_CONSUMPTION_PAYMENT_JOURNEY);
  deleteLocalStorage(CONSUMPTION_PAYMENT_JOURNEY_PATH);
  deleteLocalStorage(CONSUMPTION_JOURNEY_CODE_REDEMPTION_OBJ);
  deleteLocalStorage(SUBSCRIPTION_ORDER_SUMMARY_BANNER);
  deleteLocalStorage(PAYMENT_OBJECT);
  deleteLocalStorage(SELECTED_PLAN);
  deleteLocalStorage(PAYMENT_JOURNEY_SOURCE);
};

//TODO: make this composable
export const getPlanWithCohortNew = (plansData: SubsData) => {
  const defaultPlan = plansData?.planCategories
    ?.find((plan) => plan.isDefault)
    ?.options?.find((option) => option.isDefault);

  if (!defaultPlan) {
    return null;
  }

  if (defaultPlan.price?.discountAmount && defaultPlan?.price?.code) {
    return defaultPlan;
  }
  return (
    plansData.planCategories
      .flatMap((plan) => plan.options)
      .find(
        (option) => !!option?.price?.discountAmount && !!option?.price?.code
      ) ?? null
  );
};

export const getPlanWithCohort = (plansData: Data) => {
  if (plansData.plans.length > 0 && plansData.default_plan) {
    const cohortPlan = plansData?.plans.find(
      (plan: Plan) =>
        plan?.id === plansData?.default_plan &&
        plan.discount &&
        typeof plan.discount === 'object' &&
        Object.keys(plan.discount).length
    );
    if (cohortPlan && cohortPlan?.discount) return cohortPlan;
    else {
      return plansData.plans.find(
        (plan) =>
          plan.discount &&
          typeof plan.discount === 'object' &&
          Object.keys(plan.discount).length
      );
    }
  }
};

// Set referral paramerters id, code, name in session storage if it is present in URL
export const setReferralParametersInSession = (paramObject: ParsedUrlQuery) => {
  if (
    paramObject.id &&
    typeof paramObject.id === 'string' &&
    paramObject.code &&
    typeof paramObject.code === 'string' &&
    paramObject.name &&
    typeof paramObject.name === 'string'
  ) {
    const id = getLocalStorage(REFERRAL_ID);
    if (!id || id !== paramObject.id) {
      setLocalStorage(REFERRAL_ID, paramObject.id);
      setLocalStorage(REFERRAL_NAME, paramObject.name);
      setLocalStorage(REFERRAL_CODE, paramObject.code);
      deleteLocalStorage(REFERRAL_DISCOUNT_EXPIRY);
    }
  } else {
    const referralId = getLocalStorage(REFERRAL_ID);
    if (referralId) {
      deleteLocalStorage(REFERRAL_ID);
      deleteLocalStorage(REFERRAL_NAME);
      deleteLocalStorage(REFERRAL_CODE);
      deleteLocalStorage(REFERRAL_USERID);
    }
  }
};

// Remove fererral parameters from local storage if it is referee and discount time expired or return
export const getReferralParameters = () => {
  let name = getLocalStorage(REFERRAL_NAME);
  let id = getLocalStorage(REFERRAL_ID);
  let code = getLocalStorage(REFERRAL_CODE);
  let userId = getLocalStorage(REFERRAL_USERID);
  const discountExpiryTimestamp = getLocalStorage(REFERRAL_DISCOUNT_EXPIRY);
  if (
    name &&
    name.toString().toLowerCase() === REFERRER &&
    discountExpiryTimestamp &&
    new Date().getTime() > discountExpiryTimestamp
  ) {
    id = '';
    code = '';
    name = '';
    userId = '';
  }
  const returnObject = {
    id,
    code,
    name,
    referralUserId: userId,
    discountExpiry: discountExpiryTimestamp
  };
  return { ...returnObject };
};

export const validateReferee = () => {
  const referralData = getReferralParameters();
  const userToken = getLocalStorage(USER_TOKEN);
  const userId = parseJwt(userToken).user_id;
  const { referralUserId = '', name = '' } = referralData || {};
  if (
    name &&
    name.toString().toLowerCase() === REFERRER &&
    referralUserId === userId
  ) {
    deleteLocalStorage(REFERRAL_ID);
    deleteLocalStorage(REFERRAL_NAME);
    deleteLocalStorage(REFERRAL_CODE);
    deleteLocalStorage(REFERRAL_USERID);
    return true;
  }
  return false;
};

export const isSubscribedToContentPartner = (
  subscriptionData: subscriptionDataType,
  contentPartnerId: string
) => {
  return !!subscriptionData.currentActiveSubscriptions?.find((subscription) => {
    return !!subscription?.content_partner_details?.find((partner_details) => {
      return partner_details.content_partner_id === contentPartnerId;
    });
  });
};

export const bingeBannerEnabledCountries = [
  'US',
  'GB',
  'AU',
  'CA',
  'MY',
  'SG',
  'NZ',
  'AE',
  'SA',
  'QA',
  'OM',
  'KW',
  'BH'
];
const getFallbackState = (initialState: PlanCategory[]) => {
  return initialState.map((plan, idx) => {
    return {
      ...plan,
      active: idx === 0,
      isDefault: idx === 0,
      options: plan.options.map((option, optIdx) => ({
        ...option,
        active: optIdx === 0,
        isDefault: optIdx === 0
      }))
    };
  });
};

const getInitialState = (initialState: PlanCategory[]) => {
  return initialState.map((plan) => {
    return {
      ...plan,
      active: plan.isDefault,
      options: plan.options.map((option) => ({
        ...option,
        active: option.isDefault
      }))
    };
  });
};

/**
 * Transform api response to state
 * If Default is not present choose the first pack of first category
 */
export const transformPlanCategories = (
  initialState: PlanCategory[],
  selectedPlanId?: string
) => {
  const isDefaultPresent = initialState.some((plan) => plan.isDefault);
  if (!isDefaultPresent) {
    const state = getFallbackState(initialState);
    return state;
  }
  const transformedPlanCategories = getInitialState(initialState);
  return transformedPlanCategories;
};

export const setPlansForUpgrade = (
  plansData: SubsData,
  subscriptionID?: string
) => {
  if (plansData?.planCategories?.length && subscriptionID) {
    const upgradePlansList = plansData.planCategories
      .map((plan) => plan?.options || [])
      .flat()
      .map((option) => {
        const { price, id: planId } = option || {};
        const { actual: actual_price, renewal: sell_price } = price || {};
        return { actual_price, sell_price, planId };
      });
    setLocalStorage(ACTUAL_PRICE, upgradePlansList);
  }
};

export const checkIsAtBestPlan = (plans: SubscriptionState[] | null) => {
  return !!(
    plans?.length === 1 &&
    plans[0].options.length === 1 &&
    plans[0].options[0].isDefault &&
    !plans[0].options[0].purchaseAllowed
  );
};

export const getExpiryDate = (epoch?: string) => {
  if (!epoch) return;
  const date = new Date(epoch);
  const day = date.getUTCDate();
  const month = date.getUTCMonth();
  const year = date.getUTCFullYear();
  const suffix = dateSuffix(day);
  const expiryDate = `${day}${suffix} ${MONTH_NAMES_FULL[month]}, ${year}`;
  return expiryDate;
};

export const mapNewPaymentProviderToOld = (providers?: Paymentprovider[]) => {
  const transformed = providers?.map(
    ({ name, productReference: product_reference }) => ({
      name,
      product_reference
    })
  );
  return transformed;
};

export const transformNewPlanToOldPlan = (
  plan: OptionState | null
): Plan | null => {
  if (!plan) return null;
  const {
    id,
    title,
    purchaseAllowed: purchase_allowed,
    duration: { value: duration } = {},
    planType: plan_type,
    payment_providers,
    price: {
      currency,
      discountAmount,
      discountPercentage,
      code,
      market: display_price,
      actual: actual_price,
      renewal: sell_price
    } = {}
  } = plan;
  const newPlan = {
    id,
    title,
    purchase_allowed,
    duration,
    plan_type,
    payment_providers: mapNewPaymentProviderToOld(payment_providers),
    all_paymentProviders: mapNewPaymentProviderToOld(payment_providers),
    currency,
    suggession_tag: '',
    discount: {
      code,
      discountPercentage,
      discountAmount
    },
    features: {},
    display_price,
    sell_price,
    priority: 1,
    actual_price
  };
  return newPlan as Plan;
};

/**
 * Check if selected plan is cohort based in the discountAmount in price node
 */
export const isCohort = (selectedPlan: OptionState | null) =>
  !!selectedPlan?.price?.discountAmount && !!selectedPlan?.price?.code;

export const goToHome = () => {
  window.location.href = globalUrlHelper('/');
};

/*
 * Returns the default plan
 * If there is no default plan take the first plan from first category
 * */
export const getDefaultPlan = (plansData?: PlanCategory[] | null) => {
  return (
    plansData
      ?.find((plans) => !!plans?.isDefault)
      ?.options?.find((plan) => !!plan?.isDefault) ??
    plansData?.[0].options[0] ??
    null
  );
};

const pickImageKey = (deviceWidth = 768) => {
  if (deviceWidth < 599) return 'android';
  if (deviceWidth < 899) return 'tabletSmall';
  if (deviceWidth < 1199) return 'tableLarge';
  if (deviceWidth < 1799) return 'desktopSmall';
  return 'desktopLarge';
};

const getDimesionsString = (
  tagName: TAG_NAME,
  dimensionObject: ReturnType<typeof getImagesDimentionByResolution>
) => {
  const dimensions = dimensionObject[tagName as keyof typeof dimensionObject];
  const [w, h] = dimensions;
  return `${w ? `w_${w},` : ''}${h ? `h_${h}` : ''}`;
};

export const getSubsImageUrl = (
  deviceWidth: number,
  tagName: TAG_NAME,
  image?: OfferImage,
  isRequestedByBot = false
) => {
  if (!image) return null;
  const slug = image[pickImageKey(deviceWidth) as keyof OfferImage];
  if (!slug) return null;
  const imageParams = getWebPImageURLParams(
    isRequestedByBot ? ImageCompatibleTypes.WEBP : ImageCompatibleTypes.AVIF
  );
  const imageDimensionObj = getImagesDimentionByResolution(deviceWidth);
  const dimensionsString = getDimesionsString(tagName, imageDimensionObj);
  const url = `${CLOUDINARY_IMAGE_URL}${dimensionsString}${imageParams.replace(
    '/resources/',
    ''
  )}${slug}`;
  return url;
};

export const trackSelectedPlanMixpanelEvents = (
  plan?: OptionState,
  hasActiveSubscription = false
) => {
  mixpanelObject.setProperty(
    MIXPANEL_PACK_SELECTED,
    `${plan?.id}_${plan?.title}_${plan?.planType}`
  );
  const planCost = plan?.price?.market
    ? plan?.price?.market - plan?.price?.actual
    : plan?.price?.actual;
  mixpanelObject.setProperty(MIXPANEL_COST, planCost);

  mixpanelObject.setProperty(
    MIXPANEL_CURRENT_SUBSCRIPTION,
    hasActiveSubscription
  );

  mixpanelObject.setProperty(
    MIXPANEL_TRANSACTION_CURRENCY,
    plan?.price.currency
  );
  mixpanelObject.setProperty(
    MIXPANEL_ACTUAL_COST,
    plan?.price?.actual || 'N/A'
  );
  mixpanelObject.setProperty(MIXPANEL_ELEMENT, plan?.title);
  //This fn, internally reads from mixpanel object set above.
  // subscriptionSelectedEvent();
  timeEvent();
};
